import type { ReactNode } from 'react'
import React from 'react'

import { Skeleton } from '@fourthwall/components'

import type { StatsData } from '../../AnalyticsCard.types'
import * as S from './BarStatsTypeAnalyticsCardContent.styled'
import { Bar } from './components/Bar/Bar'
import { getMaxValueFromStatsData } from './utils/getMaxValueFromStatsData'

export const BAR_DEFAULT_COLORS = ['var(--bg-brand)', 'var(--bg-quaternary)']

export type BarStatsTypeAnalyticsCardContentProps = {
  data: StatsData[]
  isLoading?: boolean
  totalValue?: number
  formatValue?: (value: number) => ReactNode
  animation?: boolean
}

export const BarStatsTypeAnalyticsCardContent = ({
  data,
  formatValue,
  totalValue,
  isLoading = false,
  animation = true,
}: BarStatsTypeAnalyticsCardContentProps) => {
  if (isLoading) return <BarChartSkeleton />

  const maxValue = totalValue || getMaxValueFromStatsData(data)

  const renderValue = (value: number | [number, number]) => {
    if (Array.isArray(value)) {
      return (
        <>
          <Bar
            color={BAR_DEFAULT_COLORS[0]}
            value={value[0]}
            maxValue={maxValue}
            formatValue={formatValue}
            animation={animation}
          />
          <Bar
            color={BAR_DEFAULT_COLORS[1]}
            value={value[1]}
            maxValue={maxValue}
            formatValue={formatValue}
            animation={animation}
          />
        </>
      )
    }

    return (
      <Bar
        color={BAR_DEFAULT_COLORS[0]}
        value={value}
        maxValue={maxValue}
        formatValue={formatValue}
        animation={animation}
      />
    )
  }

  return (
    <S.Container>
      {data.map((item, index) => (
        <S.Item key={index}>
          <S.Label>{item.label}</S.Label>
          {renderValue(item.value)}
        </S.Item>
      ))}
    </S.Container>
  )
}

const BarChartSkeleton = () => (
  <S.Container>
    {[...Array(4)].map((_, index) => {
      const firstBarWidth = Math.max(70 - index * 15, 25)
      const secondBarWidth = Math.max(60 - index * 15, 15)

      return (
        <S.Item key={index}>
          <S.Label>
            <Skeleton.Shape width={80} height={20} />
          </S.Label>
          <S.BarContainer>
            <Skeleton.Shape width={`${firstBarWidth}%`} height={20} />
            <S.Value>
              <Skeleton.Shape width={70} height={20} />
            </S.Value>
          </S.BarContainer>
          <S.BarContainer>
            <Skeleton.Shape width={`${secondBarWidth}%`} height={20} />
            <S.Value>
              <Skeleton.Shape width={70} height={20} />
            </S.Value>
          </S.BarContainer>
        </S.Item>
      )
    })}
  </S.Container>
)

BarStatsTypeAnalyticsCardContent.Bar = Bar
